import { XMarkIcon } from "@storytell/bsky-icons";
import { type Component, For, Show, createMemo } from "solid-js";
import type { ArtifactState, ArtifactType } from "~/stores/ArtifactStore";

interface ArtifactTabsProps {
  artifacts: ArtifactState[];
  activeId?: string;
  onSelect: (id: string) => void;
  onClose: (id: string) => void;
  maxVisible?: number;
  class?: string;
}

// Icon mapping for different artifact types
const getArtifactIcon = (type: ArtifactType) => {
  switch (type) {
    case "code":
      return "{ }";
    case "image":
      return "🖼️";
    case "document":
      return "📄";
    case "data":
      return "📊";
    case "chart":
      return "📈";
    case "diagram":
      return "🔷";
    case "audio":
      return "🎵";
    default:
      return "📎";
  }
};

// Truncate long titles with ellipsis
const truncateTitle = (title: string, maxLength = 20) => {
  if (title.length <= maxLength) return title;
  return `${title.substring(0, maxLength - 3)}...`;
};

export const ArtifactTabs: Component<ArtifactTabsProps> = (props) => {
  const maxVisible = () => props.maxVisible || 8;

  // Split artifacts into visible and overflow
  const visibleArtifacts = createMemo(() => {
    const artifacts = props.artifacts;
    if (artifacts.length <= maxVisible()) {
      return artifacts;
    }

    // Always include the active artifact in visible tabs
    const activeIndex = artifacts.findIndex((a) => a.id === props.activeId);
    if (activeIndex >= 0 && activeIndex < maxVisible() - 1) {
      return artifacts.slice(0, maxVisible());
    }
    if (activeIndex >= 0) {
      // Move active to the end of visible tabs
      const others = artifacts.filter((a) => a.id !== props.activeId).slice(0, maxVisible() - 1);
      const active = artifacts[activeIndex];
      return [...others, active];
    }

    return artifacts.slice(0, maxVisible());
  });

  const overflowCount = createMemo(() => Math.max(0, props.artifacts.length - maxVisible()));

  return (
    <div
      class={`flex items-center h-10 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 ${props.class || ""}`}
    >
      {/* Artifact Tabs */}
      <div class="flex-1 flex items-center overflow-x-auto scrollbar-hide">
        <For each={visibleArtifacts()}>
          {(artifact) => (
            <ArtifactTab
              artifact={artifact}
              isActive={artifact.id === props.activeId}
              onSelect={() => props.onSelect(artifact.id)}
              onClose={() => props.onClose(artifact.id)}
            />
          )}
        </For>

        {/* Overflow indicator */}
        <Show when={overflowCount() > 0}>
          <div class="flex-shrink-0 px-3 py-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 border-l border-gray-200 dark:border-gray-600">
            +{overflowCount()} more
          </div>
        </Show>
      </div>

      {/* Panel controls */}
      <div class="flex-shrink-0 flex items-center px-2">
        <button
          onClick={() => {
            // Close all artifacts (hide panel)
            props.artifacts.forEach((artifact) => props.onClose(artifact.id));
          }}
          class="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors duration-150"
          title="Close panel"
        >
          <XMarkIcon size={16} />
        </button>
      </div>
    </div>
  );
};

interface ArtifactTabProps {
  artifact: ArtifactState;
  isActive: boolean;
  onSelect: () => void;
  onClose: () => void;
}

const ArtifactTab: Component<ArtifactTabProps> = (props) => {
  return (
    <div
      class={`
        flex-shrink-0 flex items-center group cursor-pointer border-r border-gray-200 dark:border-gray-600
        transition-colors duration-150 relative max-w-48
        ${
          props.isActive
            ? "bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            : "bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
        }
      `}
    >
      {/* Active indicator */}
      <Show when={props.isActive}>
        <div class="absolute top-0 left-0 right-0 h-0.5 bg-blue-500" />
      </Show>

      {/* Tab content */}
      <div class="flex items-center px-3 py-2 min-w-0 flex-1" onClick={props.onSelect}>
        {/* Icon */}
        <span class="flex-shrink-0 text-sm mr-2">{getArtifactIcon(props.artifact.type)}</span>

        {/* Title */}
        <span class="truncate text-sm font-medium min-w-0">{truncateTitle(props.artifact.title)}</span>

        {/* Processing indicator */}
        <Show when={props.artifact.isProcessing}>
          <div class="flex-shrink-0 ml-2">
            <div class="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
          </div>
        </Show>
      </div>

      {/* Close button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          props.onClose();
        }}
        class={`
          flex-shrink-0 p-1 mr-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600
          transition-colors duration-150 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300
          ${props.isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100"}
        `}
        title="Close artifact"
      >
        <XMarkIcon size={14} />
      </button>
    </div>
  );
};
